import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const InvoiceOrderReport = ({ customers }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
  });

  const generateReport = async (exportType = 'html') => {
    const params = new URLSearchParams();
    
    if (filters.dateFrom) {
      params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
    }
    
    if (filters.dateTo) {
      params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
    }
    
    const queryString = params.toString();
    
    setLoadingReport(true);
    try {
      const response = await api.get(
        `/reports/invoice-order-report${queryString ? `?${queryString}` : ''}`
      );
      
      if (response.data && response.data.status) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";
        
        if (exportType === 'excel') {
          const excelData = response.data.report.map(invoice => ({
            "Style": invoice.style,
            "Item": invoice.item,
            "Party": invoice.party,
            "Quantity": invoice.quantity,
            "Rate":invoice.rate,
            "Amount":invoice.amount,
            
            "GST":invoice?.gst,
            "QST":invoice?.qst,
            "HST":invoice?.hst,
            "Final Price":invoice.final_price,
            "Ref #":invoice.ref_num
          }));
          
          exportToExcel(excelData, `Invoice_Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Invoice Sheet</title>
            <style>
               body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin: 15px; font-size: 14px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Invoice Sheet</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
                <p><strong>Sheet # = ${response.data?.sh_no}</p>

                <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
              </div>
              
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Style</th>
                  <th>Item</th>
                  <th>Customer</th>
                  <th>Quantity</th>
                  <th>Rate</th>
                  <th>Amount</th>
                   <th>GST</th>
                    <th>QST</th>
                     <th>HST</th>
                  <th>Total</th>
                  <th>Ref #</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        response.data.report.forEach(invoice => {
          htmlContent += `
            <tr>
              <td>${invoice?.style}</td>
              <td>${invoice?.item}</td>
              <td>${invoice?.party}</td>
              <td>${invoice?.quantity}</td>
              <td>${invoice?.rate}</td>
              <td>${invoice?.amount}</td>
              <td>${invoice?.gst}</td>
              <td>${invoice?.qst}</td>
              <td>${invoice?.hst}</td>
               
              <td>${invoice.final_price}</td>
              <td>${invoice.ref_num}</td>
            
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;
        
        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Invoice_Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }
        
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }
        
        reportWindow.document.write(htmlContent);
        reportWindow.document.close();
        
        window.addEventListener('message', function(event) {
          if (event.data.type === 'exportPDF' && event.data.reportType === 'invoiceOrder') {
            generateReport('pdf');
          } else if (event.data.type === 'exportExcel' && event.data.reportType === 'invoiceOrder') {
            generateReport('excel');
          }
        });
        
        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating invoice order report:", error);
      toast.error("Failed to generate invoice order report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('date_from', filters.dateFrom.format("YYYY-MM-DD"));
      if (filters.dateTo) params.append('date_to', filters.dateTo.format("YYYY-MM-DD"));
      
      const queryString = params.toString();
      
      const response = await api.get(`/reports/invoice-order-report${queryString ? `?${queryString}` : ''}`);
      
      if (response.data && response.data.status) {
        const dateFromStr = filters.dateFrom ? filters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = filters.dateTo ? filters.dateTo.format("YYYY-MM-DD") : "All";
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Invoice Sheet</title>
            <style>
                body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Invoice Sheet</h1>
              <div class="report-filters">
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Style</th>
                  <th>Item</th>
                  <th>Customer</th>
                  <th>Quantity</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th>GST</th>
                  <th>QST</th>

                  <th>HST</th>

                  <th>Total</th>
                  <th>Ref #</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        response.data.report.forEach(invoice => {
          htmlContent += `
            <tr>
              <td>${invoice.style}</td>
              <td>${invoice.item}</td>
              <td>${invoice.party}</td>
              <td>${invoice.quantity}</td>
               <td>${invoice.rate}</td>
              <td>${invoice.amount}</td>
                <td>${invoice?.gst}</td>
              <td>${invoice?.qst}</td>
              <td>${invoice?.hst}</td>
              <td>${invoice.final_price}</td>
              <td>${invoice.ref_num}</td>
            </tr>
          `;
        });
        
        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Invoice_Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={5}>
        <DatePicker
          label="From Date"
          value={filters.dateFrom}
          onChange={(newValue) => setFilters({ ...filters, dateFrom: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={5}>
        <DatePicker
          label="To Date"
          value={filters.dateTo}
          onChange={(newValue) => setFilters({ ...filters, dateTo: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      
      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateReport('html')}
            disabled={loadingReport || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{ 
              height: "38px", 
              backgroundColor: colors.primary, 
              '&:hover': { 
                backgroundColor: colors.primary, 
                opacity: 0.9 
              } 
            }}
          >
            {loadingReport ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('pdf')}
            disabled={loadingReport || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('excel')}
            disabled={loadingReport || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('email')}
            disabled={loadingReport || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default InvoiceOrderReport;
